import { ApiService, type ApiRequestConfig } from "./api.service";
import type { ExportStatus } from "@/components/data-table/use-csv-export";
import type {
  IWorkspace,
  IWorkspaceMember,
  IWorkspaceMemberInvitation,
  IWorkspaceBulkInviteFormData,
} from "@/types/workspace";
import type { TEntitySearchParams, TEntitySearchResponse } from "@/types/search";
import type { TUserWorkHours } from "@/types/user";
import type {
  TUserRate,
  TUserRateCreate,
  TUserRateUpdate,
  TJobRoleRate,
  TJobRoleRateCreate,
  TJobRoleRateUpdate,
} from "@/types/rate";
import type { TUserImportRequest, TUserImportResult } from "@/types/user-import";
import { normalizeWorkHours } from "@/services/work-hours.service";
import type { components } from "@/types/api.generated";

/** One member's "Can be assigned as" answers (NG-3897), as the API returns them. */
export type MemberEligibility = components["schemas"]["MemberEligibility"];
/** One relationship's answer within {@link MemberEligibility}. */
export type MemberEligibilityEntry = MemberEligibility["relationships"][number];
/** Relationship to true (allow), false (deny) or null (clear: the role decides). */
export type MemberEligibilityOverrides = Record<string, boolean | null>;

export type MemberProfile = {
  id: string;
  first_name: string;
  last_name: string;
  display_name: string;
  email: string;
  avatar_url: string | null;
  mobile_number: string | null;
  user_timezone: string;
  country_region: string;
};

type RawWorkspaceMember = Omit<IWorkspaceMember, "capacity_percent"> & {
  capacity_percent?: number | string | null;
};

/**
 * Summary of the rows wiped (``before``) and rebuilt (``after``) when a demo
 * workspace is reset to its canonical seeded state. The count maps are keyed by
 * entity name. Returned on the status endpoint once the async reset succeeds.
 */
export interface IWorkspaceResetResult {
  success?: boolean;
  workspace_id: string;
  slug: string;
  before: Record<string, number>;
  after: Record<string, number>;
}

/** Body of the 202 returned by ``POST /api/workspaces/<slug>/reset/`` — the id
 *  of the enqueued Celery task to poll. */
interface IWorkspaceResetEnqueued {
  task_id: string;
  status: string;
}

/** Celery task state surfaced by the reset status endpoint. */
type TWorkspaceResetState = "PENDING" | "STARTED" | "RETRY" | "SUCCESS" | "FAILURE";

/** Body of ``GET /api/workspaces/<slug>/reset/<task_id>/`` — the task state and,
 *  on SUCCESS, the reset summary (``error`` carries the message on FAILURE). */
interface IWorkspaceResetStatus {
  task_id: string;
  state: TWorkspaceResetState;
  result: IWorkspaceResetResult | null;
  error: string | null;
}

/**
 * Summary of a completed date realignment (NG-961): how far the timeline moved,
 * where it now sits, and how many rows were rewritten per model label.
 */
export interface IWorkspaceDateRealignResult {
  shift_days: number;
  previous_anchored_at: string;
  anchored_at: string;
  rows_changed: Record<string, number>;
  total_rows_changed: number;
}

/** Body of the 202 from ``POST /api/workspaces/<slug>/realign-dates/``. The
 *  shift is already known at enqueue time, so the UI can say what is happening
 *  while it waits rather than only once the job lands. */
interface IWorkspaceDateRealignEnqueued {
  task_id: string;
  status: string;
  shift_days: number;
  anchored_at: string;
}

/** The shared reset status body, carrying a realignment summary on SUCCESS. */
interface IWorkspaceDateRealignStatus {
  task_id: string;
  state: TWorkspaceResetState;
  result: IWorkspaceDateRealignResult | null;
  error: string | null;
}

/** How often to poll the reset status endpoint while the job runs, in ms. */
const RESET_POLL_INTERVAL_MS = 3000;
/** Max number of polls before giving up. The reset takes ~7.5 min, so at a 3s
 *  interval ~200 attempts (~10 min) leaves generous headroom. */
const RESET_POLL_MAX_ATTEMPTS = 200;

/** How often to poll while a date realignment runs, in ms. */
const REALIGN_POLL_INTERVAL_MS = 2000;
/** Max polls before giving up. The job is one UPDATE per dated model — minutes
 *  of headroom, not the reset's ten. */
const REALIGN_POLL_MAX_ATTEMPTS = 90;

/** Polling knobs shared by the async workspace jobs — mirrors the CSV-export
 *  poller so polling semantics (and the injectable wait) stay consistent. */
export interface IWorkspacePollOptions {
  /** Delay between polls, ms. */
  intervalMs?: number;
  /** Give up after this many polls. */
  maxAttempts?: number;
  /** Injectable wait (tests pass an immediate resolver). */
  wait?: (ms: number) => Promise<void>;
}

/** Options for {@link WorkspaceService.resetWorkspace}. */
export interface IWorkspaceResetPollOptions extends IWorkspacePollOptions {
  /** Reset to a specific workspace template instead of the canonical default.
   *  Omitted ⇒ the existing default reset. */
  templateId?: string;
  /** Reset to a specific retained version of `templateId` instead of its active
   *  version. Omitted ⇒ the template's active version (today's behaviour). */
  version?: number;
}

/** Raised by ``resetWorkspace`` when polling exhausts its attempts without a
 *  terminal state, so the caller can surface a timeout-specific error distinct
 *  from a backend FAILURE. */
export class WorkspaceResetTimeoutError extends Error {
  constructor(slug: string) {
    super(`Timed out waiting for workspace "${slug}" reset to finish`);
    this.name = "WorkspaceResetTimeoutError";
  }
}

/** Raised when the reset task reports FAILURE — carries the backend error
 *  message so the UI can surface it. */
export class WorkspaceResetFailedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "WorkspaceResetFailedError";
  }
}

export interface IWorkspaceMembersPage {
  results: IWorkspaceMember[];
  next_cursor: string;
  prev_cursor: string;
  next_page_results: boolean;
  prev_page_results: boolean;
  total_count: number;
  count: number;
}

export interface IWorkspaceMembersListQuery {
  cursor?: string | null;
  q?: string;
  role?: string[];
  /** License Tier codes (guest / standard / admin). Resolved server-side to the
   *  workspace's role slugs at those tiers — a tier is derived from the System
   *  Role, not stored on the member (NG-1236/NG-1249) — and filtered there
   *  rather than here because the list is paginated server-side. */
  tier?: string[];
  /** Job role / Qualifications (skill) / Team ids (NG-3222). Each is resolved
   *  server-side — the facts live off the member row and the list is paginated
   *  there. The Teams filter is honoured only for a `team.read` holder, the same
   *  gate the Teams column has. */
  job_role?: string[];
  skills?: string[];
  teams?: string[];
  order_by?: string;
  per_page?: number;
}

/** The Members table's filters and sort, without its paging. */
type IWorkspaceMembersFilterQuery = Omit<IWorkspaceMembersListQuery, "cursor" | "per_page">;

/**
 * Append the Members filters and sort to `params`. The list and the export both
 * build their query string here, because the backend serves them from one member
 * queryset: an export must carry every filter the list does, or the file stops
 * matching the table (NG-2997 dropped `tier` from the export alone).
 */
function appendMembersFilterParams(params: URLSearchParams, options: IWorkspaceMembersFilterQuery) {
  if (options.q) params.set("q", options.q);
  if (options.order_by) params.set("order_by", options.order_by);
  for (const r of options.role ?? []) params.append("role", r);
  for (const t of options.tier ?? []) params.append("tier", t);
  for (const jr of options.job_role ?? []) params.append("job_role", jr);
  for (const s of options.skills ?? []) params.append("skills", s);
  for (const team of options.teams ?? []) params.append("teams", team);
}

function normalizeWorkspaceMember(raw: RawWorkspaceMember): IWorkspaceMember {
  const capacityPercentValue =
    typeof raw.capacity_percent === "string"
      ? Number.parseFloat(raw.capacity_percent)
      : typeof raw.capacity_percent === "number"
        ? raw.capacity_percent
        : Number.NaN;

  return {
    ...raw,
    capacity_percent: Number.isFinite(capacityPercentValue) ? capacityPercentValue : 100,
  };
}

export class WorkspaceService extends ApiService {
  async listWorkspaces(): Promise<IWorkspace[]> {
    return this.get("/api/users/me/workspaces/");
  }

  async getWorkspace(slug: string): Promise<IWorkspace> {
    return this.get(`/api/workspaces/${slug}/`);
  }

  async createWorkspace(data: Partial<IWorkspace>): Promise<IWorkspace> {
    return this.post("/api/workspaces/", data);
  }

  async updateWorkspace(slug: string, data: Partial<IWorkspace>): Promise<IWorkspace> {
    return this.patch(`/api/workspaces/${slug}/`, data);
  }

  async deleteWorkspace(slug: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/`);
  }

  /**
   * Turn the FR20 SSO password-login override on or off for a workspace
   * (NG-1371).
   *
   * Its own endpoint rather than a field on {@link updateWorkspace}: the dial is admin-only
   * (`workspace.sso.override`) while the workspace PATCH is `workspace.edit`,
   * which power users also hold. Returns the whole workspace so the caller can
   * adopt it as the new baseline in one round trip.
   */
  async setSsoOverrideEnabled(slug: string, enabled: boolean): Promise<IWorkspace> {
    return this.patch(`/api/workspaces/${slug}/sso-override/`, { enabled });
  }

  /**
   * Reset a demo workspace back to its canonical seeded state (wipes + re-seeds).
   *
   * The wipe + re-seed takes ~7.5 minutes — far past proxy/gateway timeouts — so
   * the backend runs it as a Celery job: this POST returns `202` with a
   * `task_id`, and we then poll the status endpoint every `intervalMs` until the
   * task reaches a terminal state (or `maxAttempts` is exhausted).
   *
   * Resolves with the reset summary on SUCCESS. Rejects with:
   *  - the original Axios error on a non-202 POST (e.g. the 400 "not a demo
   *    workspace" case the caller surfaces verbatim),
   *  - {@link WorkspaceResetFailedError} when the task reports FAILURE, or
   *  - {@link WorkspaceResetTimeoutError} if polling exhausts its attempts.
   *
   * Gated server-side on `workspace.reset`.
   */
  async resetWorkspace(
    slug: string,
    {
      intervalMs = RESET_POLL_INTERVAL_MS,
      maxAttempts = RESET_POLL_MAX_ATTEMPTS,
      wait,
      templateId,
      version,
    }: IWorkspaceResetPollOptions = {},
  ): Promise<IWorkspaceResetResult> {
    const delay = wait ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
    // `template_id` is optional: omitted ⇒ canonical default reset, present ⇒
    // rebuild from the chosen workspace template. `version` (only meaningful with
    // a `template_id`) targets a past retained version; omitted ⇒ active version.
    const body = templateId
      ? { template_id: templateId, ...(version !== undefined ? { version } : {}) }
      : undefined;
    const { task_id } = await this.post<IWorkspaceResetEnqueued>(
      `/api/workspaces/${slug}/reset/`,
      body,
    );
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await delay(intervalMs);
      const status = await this.getResetStatus(slug, task_id);
      if (status.state === "SUCCESS") {
        if (!status.result) {
          throw new WorkspaceResetFailedError(
            `Workspace "${slug}" reset succeeded but returned no summary`,
          );
        }
        return status.result;
      }
      if (status.state === "FAILURE") {
        throw new WorkspaceResetFailedError(status.error ?? `Workspace "${slug}" reset failed`);
      }
    }
    throw new WorkspaceResetTimeoutError(slug);
  }

  /** Poll a single async workspace-reset task's status by id. */
  async getResetStatus(slug: string, taskId: string): Promise<IWorkspaceResetStatus> {
    return this.get(`/api/workspaces/${slug}/reset/${taskId}/`);
  }

  /**
   * Move the workspace's content dates onto today, keeping its content (NG-961).
   *
   * The non-destructive sibling of {@link resetWorkspace}: it shifts the dates
   * that are already there instead of wiping and rebuilding, so everything the
   * rep added since the last reset survives and moves with the rest.
   *
   * Same enqueue-and-poll shape, and deliberately the same status endpoint — it
   * is generic over a Celery task id. The job is far cheaper than a reset (one
   * UPDATE per dated model), so the poll budget is minutes rather than ten.
   *
   * Resolves with the summary on SUCCESS. Rejects with the original Axios error
   * on a non-202 POST, {@link WorkspaceResetFailedError} on task FAILURE, or
   * {@link WorkspaceResetTimeoutError} if polling runs out.
   *
   * Gated server-side on `workspace.reset`.
   */
  async realignWorkspaceDates(
    slug: string,
    {
      intervalMs = REALIGN_POLL_INTERVAL_MS,
      maxAttempts = REALIGN_POLL_MAX_ATTEMPTS,
      wait,
    }: IWorkspacePollOptions = {},
  ): Promise<IWorkspaceDateRealignResult> {
    const delay = wait ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
    const { task_id } = await this.post<IWorkspaceDateRealignEnqueued>(
      `/api/workspaces/${slug}/realign-dates/`,
      {},
    );
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await delay(intervalMs);
      const status = await this.getRealignStatus(slug, task_id);
      if (status.state === "SUCCESS") {
        if (!status.result) {
          throw new WorkspaceResetFailedError(
            `Workspace "${slug}" date realignment succeeded but returned no summary`,
          );
        }
        return status.result;
      }
      if (status.state === "FAILURE") {
        throw new WorkspaceResetFailedError(
          status.error ?? `Workspace "${slug}" date realignment failed`,
        );
      }
    }
    throw new WorkspaceResetTimeoutError(slug);
  }

  /** Poll a single async date-realignment task by id (shared status endpoint). */
  async getRealignStatus(slug: string, taskId: string): Promise<IWorkspaceDateRealignStatus> {
    return this.get(`/api/workspaces/${slug}/reset/${taskId}/`);
  }

  async checkSlug(slug: string): Promise<{ status: boolean }> {
    return this.get(`/api/workspace-slug-check/?slug=${slug}`);
  }

  /** What the create form may offer the caller: the creatable types and the default, from the server's own rules (NG-3422). */
  async getCreateOptions(): Promise<{ workspace_types: string[]; default_type: string | null }> {
    return this.get("/api/workspace-create-options/");
  }

  async listMembers(slug: string, config?: ApiRequestConfig): Promise<IWorkspaceMember[]> {
    const raw = await this.get<RawWorkspaceMember[]>(`/api/workspaces/${slug}/members/`, config);
    return raw.map(normalizeWorkspaceMember);
  }

  /** Paginated members fetch for the management list (server filters/sort +
   *  cursor pagination). Distinct from ``listMembers`` (the full deduped bank
   *  every member-picker still uses). */
  async listMembersPage(
    slug: string,
    options: IWorkspaceMembersListQuery = {},
    config?: ApiRequestConfig,
  ): Promise<IWorkspaceMembersPage> {
    const params = new URLSearchParams();
    params.set("per_page", String(options.per_page ?? 50));
    if (options.cursor) params.set("cursor", options.cursor);
    appendMembersFilterParams(params, options);
    const page = await this.get<
      Omit<IWorkspaceMembersPage, "results"> & { results: RawWorkspaceMember[] }
    >(`/api/workspaces/${slug}/members/?${params.toString()}`, config);
    return { ...page, results: page.results.map(normalizeWorkspaceMember) };
  }

  /**
   * Export the Members list as CSV, scoped to the same filters/sort as the list
   * (`options`) and, when `body.ids` is set, restricted to those rows. Returns
   * the raw Response so the caller can stream the CSV (200) or poll the async
   * token (202).
   */
  async exportMembers(
    slug: string,
    body: { ids?: string[]; columns?: string[] },
    options: IWorkspaceMembersFilterQuery = {},
  ): Promise<Response> {
    const params = new URLSearchParams();
    appendMembersFilterParams(params, options);
    const qs = params.toString();
    return this.postRaw(`/api/workspaces/${slug}/export-members/${qs ? `?${qs}` : ""}`, body);
  }

  async getExportStatus(slug: string, token: string): Promise<ExportStatus> {
    return this.get(`/api/workspaces/${slug}/export-status/${token}/`);
  }

  async updateMember(
    slug: string,
    memberId: string,
    data: Partial<IWorkspaceMember>,
  ): Promise<IWorkspaceMember> {
    const raw = await this.patch<RawWorkspaceMember>(
      `/api/workspaces/${slug}/members/${memberId}/`,
      data,
    );
    return normalizeWorkspaceMember(raw);
  }

  async deleteMember(slug: string, memberId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/members/${memberId}/`);
  }

  /** Bulk-import workspace members from parsed CSV rows (NG-1296). Returns the
   *  per-row report (created / skipped / error + issues) so the importer can show
   *  partial success. Distinct from `inviteMembers`: an import creates the account
   *  outright rather than mailing an invitation. */
  async bulkImportUsers(slug: string, body: TUserImportRequest): Promise<TUserImportResult> {
    return this.post(`/api/workspaces/${slug}/members/import/`, body);
  }

  async listInvitations(
    slug: string,
    config?: ApiRequestConfig,
  ): Promise<IWorkspaceMemberInvitation[]> {
    // Server-paginated (issue #1254); walk the pages so the invitations panel
    // keeps the full set it renders locally. UI is unchanged.
    return this.getAllPages<IWorkspaceMemberInvitation>(
      ({ cursor, perPage }) => {
        const qs = new URLSearchParams({ per_page: String(perPage) });
        if (cursor) qs.set("cursor", cursor);
        return `/api/workspaces/${slug}/invitations/?${qs}`;
      },
      undefined,
      config,
    );
  }

  async inviteMembers(
    slug: string,
    data: IWorkspaceBulkInviteFormData,
  ): Promise<IWorkspaceMemberInvitation[]> {
    return this.post(`/api/workspaces/${slug}/invitations/`, data);
  }

  async updateInvitation(
    slug: string,
    invitationId: string,
    data: Partial<IWorkspaceMemberInvitation>,
  ): Promise<IWorkspaceMemberInvitation> {
    return this.patch(`/api/workspaces/${slug}/invitations/${invitationId}/`, data);
  }

  async deleteInvitation(slug: string, invitationId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/invitations/${invitationId}/`);
  }

  /** Re-send a pending invite's email and restart its expiry clock (NG-2490). */
  async resendInvitation(slug: string, invitationId: string): Promise<IWorkspaceMemberInvitation> {
    return this.post(`/api/workspaces/${slug}/invitations/${invitationId}/resend/`, {});
  }

  async getLastActiveWorkspace(): Promise<{
    workspace_details: IWorkspace;
    project_details?: Array<{ member: string; role: string; id: string | null }>;
  }> {
    return this.get("/api/users/last-visited-workspace/");
  }

  async fetchEligibleUsers(
    slug: string,
    role: string,
    config?: ApiRequestConfig,
  ): Promise<string[]> {
    return this.get<{ eligible_user_ids: string[] }>(`/api/workspaces/${slug}/eligible-users/`, {
      ...config,
      params: { role },
    }).then((data) => data?.eligible_user_ids ?? []);
  }

  /** One member's eligibility and their overrides (NG-3897). */
  async fetchMemberEligibility(
    slug: string,
    memberId: string,
    config?: ApiRequestConfig,
  ): Promise<MemberEligibility> {
    return this.get<MemberEligibility>(
      `/api/workspaces/${slug}/members/${memberId}/eligibility/`,
      config,
    );
  }

  /** Sets, changes or clears a member's eligibility overrides, all or nothing (NG-3897). */
  async updateMemberEligibility(
    slug: string,
    memberId: string,
    overrides: MemberEligibilityOverrides,
  ): Promise<MemberEligibility> {
    return this.put<MemberEligibility>(`/api/workspaces/${slug}/members/${memberId}/eligibility/`, {
      overrides,
    });
  }

  async fetchMemberEligibleRoles(
    slug: string,
    memberId: string,
    config?: ApiRequestConfig,
  ): Promise<
    Record<
      string,
      {
        eligible: boolean;
        blocking_roles: {
          role: string;
          resource_type: string;
          resource_id: string;
          resource_name: string;
        }[];
      }
    >
  > {
    return this.get<{
      roles: Record<
        string,
        {
          eligible: boolean;
          blocking_roles: {
            role: string;
            resource_type: string;
            resource_id: string;
            resource_name: string;
          }[];
        }
      >;
    }>(`/api/workspaces/${slug}/members/${memberId}/eligible-roles/`, config).then(
      (res) => res?.roles ?? {},
    );
  }

  async searchWorkspaceEntities(
    slug: string,
    params: TEntitySearchParams,
    options?: { signal?: AbortSignal },
  ): Promise<TEntitySearchResponse> {
    return this.get(`/api/workspaces/${slug}/entity-search/`, {
      params: {
        search: params.search,
        workspace_search: params.workspace_search,
        ...(params.project_id ? { project_id: params.project_id } : {}),
        ...(params.type ? { type: params.type.join(",") } : {}),
      },
      signal: options?.signal,
    });
  }

  async getMemberWorkHours(
    slug: string,
    memberId: string,
    config?: ApiRequestConfig,
  ): Promise<TUserWorkHours> {
    const raw = await this.get<Record<string, unknown>>(
      `/api/workspaces/${slug}/members/${memberId}/work-hours/`,
      config,
    );
    return normalizeWorkHours(raw);
  }

  async updateMemberWorkHours(
    slug: string,
    memberId: string,
    data: Partial<TUserWorkHours>,
  ): Promise<TUserWorkHours> {
    const raw = await this.patch<Record<string, unknown>>(
      `/api/workspaces/${slug}/members/${memberId}/work-hours/`,
      data,
    );
    return normalizeWorkHours(raw);
  }

  async getMemberRates(
    slug: string,
    memberId: string,
    config?: ApiRequestConfig,
  ): Promise<TUserRate[]> {
    return this.get<TUserRate[]>(`/api/workspaces/${slug}/members/${memberId}/rates/`, config);
  }

  async setMemberRate(slug: string, memberId: string, data: TUserRateCreate): Promise<TUserRate> {
    return this.post<TUserRate>(`/api/workspaces/${slug}/members/${memberId}/rates/`, data);
  }

  async updateMemberRate(
    slug: string,
    memberId: string,
    rateId: string,
    data: TUserRateUpdate,
  ): Promise<TUserRate> {
    return this.patch<TUserRate>(
      `/api/workspaces/${slug}/members/${memberId}/rates/${rateId}/`,
      data,
    );
  }

  async deleteMemberRate(slug: string, memberId: string, rateId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/members/${memberId}/rates/${rateId}/`);
  }

  async getJobRoleRates(
    slug: string,
    jobRoleId: string,
    config?: ApiRequestConfig,
  ): Promise<TJobRoleRate[]> {
    return this.get<TJobRoleRate[]>(
      `/api/workspaces/${slug}/job-roles/${jobRoleId}/rates/`,
      config,
    );
  }

  async setJobRoleRate(
    slug: string,
    jobRoleId: string,
    data: TJobRoleRateCreate,
  ): Promise<TJobRoleRate> {
    return this.post<TJobRoleRate>(`/api/workspaces/${slug}/job-roles/${jobRoleId}/rates/`, data);
  }

  async updateJobRoleRate(
    slug: string,
    jobRoleId: string,
    rateId: string,
    data: TJobRoleRateUpdate,
  ): Promise<TJobRoleRate> {
    return this.patch<TJobRoleRate>(
      `/api/workspaces/${slug}/job-roles/${jobRoleId}/rates/${rateId}/`,
      data,
    );
  }

  async deleteJobRoleRate(slug: string, jobRoleId: string, rateId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/job-roles/${jobRoleId}/rates/${rateId}/`);
  }

  async getMemberProfile(
    slug: string,
    memberId: string,
    config?: ApiRequestConfig,
  ): Promise<MemberProfile> {
    return this.get<MemberProfile>(`/api/workspaces/${slug}/members/${memberId}/profile/`, config);
  }

  async updateMemberProfile(
    slug: string,
    memberId: string,
    data: Partial<MemberProfile>,
  ): Promise<MemberProfile> {
    return this.patch<MemberProfile>(`/api/workspaces/${slug}/members/${memberId}/profile/`, data);
  }
}
